// v10/src/components/admin/AdminHobbsReport.js
// FIX i18n: useTranslation hook agregado al componente principal
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader, AlertCircle, Plane, Clock, Users, Wrench, Download, MapPin, FileSpreadsheet, BookOpen, Upload as UploadIcon, ArrowDownUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import api from '../../services/apiService';
import PeriodPicker from '../PeriodPicker';
import useIsMobile from '../../hooks/useIsMobile';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

// PR #5 — Libro de vuelo
const PURPOSE_LABELS = {
  ENT: 'Entrenamiento', VP: 'Vuelo personal', ADAP: 'Adaptación',
  READAP: 'Readaptación', INST: 'Instrucción',
  Bautismo: 'Bautismo', Excursion: 'Excursión', Otros: 'Otros',
};
const purposeLabel = (e) => {
  if (!e?.purpose) return '—';
  if (e.purpose === 'Otros') return e.purpose_other ? `Otros: ${e.purpose_other}` : 'Otros';
  return PURPOSE_LABELS[e.purpose] || e.purpose;
};

const StatCard = ({ icon, label, value, sub, color, darkMode }) => (
  <div style={{ padding: '16px', borderRadius: '16px', background: darkMode ? 'rgba(255,255,255,0.04)' : 'white', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '14px' }}>
    <div style={{ width: '44px', height: '44px', borderRadius: '14px', background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {React.cloneElement(icon, { style: { width: '22px', height: '22px', color } })}
    </div>
    <div>
      <p style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: darkMode ? 'white' : '#0f172a', lineHeight: 1 }}>{value}</p>
      <p style={{ margin: '2px 0 0', fontSize: '12px', fontWeight: 600, color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>{label}</p>
      {sub && <p style={{ margin: '1px 0 0', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8' }}>{sub}</p>}
    </div>
  </div>
);

const AdminHobbsReport = ({ darkMode, user }) => {
  const { t } = useTranslation('admin');
  const isMobile = useIsMobile();
  const now   = new Date();
  const [year, setYear]           = useState(now.getFullYear());
  const [month, setMonth]         = useState(now.getMonth() + 1);
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [tab, setTab]             = useState('aircraft');
  const [flightLog, setFlightLog] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingXls, setExportingXls] = useState(false);
  // PR #6 (Vision) — filtro para ocultar vuelos importados de planillas
  const [showImported, setShowImported] = useState(true);
  // Mini-PR pulido — orden por fecha del libro de vuelos
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = más reciente primero

  // ── GPS — aeroclub ──────────────────────────────────────────────
  const [clubName, setClubName]     = useState('');
  const [loadingGeo, setLoadingGeo] = useState(true);

  useEffect(() => {
    if (!navigator.geolocation) { setLoadingGeo(false); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lon } = pos.coords;
        try {
          const API   = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          const token = (() => {
            try {
              const u = JSON.parse(
                localStorage.getItem('condor360_current_user') ||
                sessionStorage.getItem('condor360_current_user') || '{}'
              );
              return u.token || '';
            } catch { return ''; }
          })();
          const res  = await fetch(`${API}/api/airports/nearest?lat=${lat}&lng=${lon}&radius=15`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const json = await res.json();
          if (json.success && json.found && json.data?.name) setClubName(json.data.name);
        } catch { }
        finally { setLoadingGeo(false); }
      },
      () => setLoadingGeo(false),
      { timeout: 6000 }
    );
  }, []);

  // ── Carga del reporte ───────────────────────────────────────────
  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get(`/api/reports/hobbs?year=${year}&month=${month}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar el reporte');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year, month]);

  const loadFlightLog = async () => {
    setLoadingLog(true);
    try {
      const res = await api.get(`/api/reports/flight-log?year=${year}&month=${month}`);
      setFlightLog(res.data);
    } catch (err) {
      console.error('Error cargando libro de vuelos:', err);
    } finally { setLoadingLog(false); }
  };

  useEffect(() => {
    if (tab === 'flightlog') loadFlightLog();
  }, [tab, year, month]);

  // ── Exportar PDF ────────────────────────────────────────────────
  const handleExportPDF = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const pdf  = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W    = pdf.internal.pageSize.getWidth();
      const H    = pdf.internal.pageSize.getHeight();

      const NAVY   = [15, 40, 80];
      const BLUE   = [37, 99, 235];
      const PURPLE = [124, 58, 237];
      const TEAL   = [8, 145, 178];
      const GREEN  = [22, 163, 74];
      const RED    = [239, 68, 68];
      const AMBER  = [217, 119, 6];
      const GRAY   = [100, 116, 139];
      const LGRAY  = [241, 245, 249];
      const BLACK  = [15, 23, 42];
      const WHITE  = [255, 255, 255];

      const monthLabel    = `${MONTHS[month - 1]} ${year}`;
      const generated     = now.toLocaleDateString('es-AR');
      const generatedFull = now.toLocaleString('es-AR', { dateStyle: 'long', timeStyle: 'short' });

      const setColor = (rgb) => pdf.setTextColor(...rgb);
      const setFill  = (rgb) => pdf.setFillColor(...rgb);
      const setDraw  = (rgb) => pdf.setDrawColor(...rgb);
      const bold     = (s = 10) => { pdf.setFont('helvetica', 'bold');   pdf.setFontSize(s); };
      const normal   = (s = 9)  => { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(s); };
      const small    = (s = 7)  => { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(s); };

      // Header
      setFill(NAVY); pdf.rect(0, 0, W, 22, 'F');
      setFill(BLUE); pdf.circle(16, 11, 6, 'F');
      bold(8); setColor(WHITE); pdf.text('C360', 16, 13, { align: 'center' });
      bold(13); setColor(WHITE); pdf.text('CONDOR360', 26, 9);
      normal(7); setColor([147, 197, 253]); pdf.text('Aeroclubes y Escuelas de Aviación', 26, 14);
      bold(10); setColor(WHITE); pdf.text(`${t('hobbs_report_export_pdf_report_title')} — ${monthLabel}`, W / 2, 9, { align: 'center' });
      small(7); setColor([147, 197, 253]); pdf.text(`${t('hobbs_report_export_pdf_generated_label')}: ${generatedFull}`, W / 2, 14, { align: 'center' });

      // Metadatos
      let y = 26;
      setFill(LGRAY); setDraw([226, 232, 240]);
      pdf.roundedRect(14, y, W - 28, 18, 3, 3, 'FD');
      setFill(BLUE); pdf.roundedRect(14, y, 3, 18, 1.5, 1.5, 'F');
      const colMeta = (W - 28 - 6) / 3;
      [
        { label: t('hobbs_report_export_pdf_meta_club'),         value: clubName || t('hobbs_report_export_pdf_no_club'), color: clubName ? BLACK : GRAY },
        { label: t('hobbs_report_export_pdf_meta_generated_by'), value: user?.name || t('hobbs_report_export_pdf_default_admin'), color: BLACK },
        { label: t('hobbs_report_export_pdf_meta_period'),       value: monthLabel, color: BLUE },
      ].forEach((m, i) => {
        const x = 20 + i * (colMeta + 2);
        small(6.5); setColor(GRAY); pdf.text(m.label, x, y + 7);
        bold(8); setColor(m.color); pdf.text(m.value, x, y + 13);
      });
      y += 24;

      // Stats cards
      bold(13); setColor(BLACK); pdf.text(t('hobbs_report_export_pdf_summary_title'), 14, y);
      y += 2; setFill(BLUE); pdf.rect(14, y, 30, 0.8, 'F'); y += 6;
      const stats = [
        { label: t('hobbs_report_export_pdf_stat_hours_flown'),     value: `${Math.round(data.totals.total_hours * 10) / 10} hs`, sub: `${data.totals.total_flights} vuelos`, color: BLUE   },
        { label: t('hobbs_report_export_pdf_stat_active_aircraft'), value: String(data.totals.active_aircraft),                   sub: `de ${data.aircraft.length} en flota`, color: PURPLE },
        { label: t('pilots_flew'),   value: String(data.pilots.length),                            sub: t('in_this_period'), color: TEAL },
        { label: t('need_service'), value: String(data.totals.aircraft_needing_service),          sub: t('overdue_or_upcoming'), color: data.totals.aircraft_needing_service > 0 ? RED : GREEN },
      ];
      const cardW = (W - 14 * 2 - 6 * 3) / 4;
      stats.forEach((s, i) => {
        const x = 14 + i * (cardW + 6);
        setFill(LGRAY); setDraw([226, 232, 240]); pdf.roundedRect(x, y, cardW, 22, 3, 3, 'FD');
        setFill(s.color); pdf.roundedRect(x, y, 3, 22, 1.5, 1.5, 'F');
        bold(14); setColor(BLACK); pdf.text(s.value, x + 7, y + 9);
        bold(6.5); setColor(s.color); pdf.text(s.label.toUpperCase(), x + 7, y + 14);
        small(6); setColor(GRAY); pdf.text(s.sub, x + 7, y + 18.5);
      });
      y += 30;

      // Tabla aeronaves
      bold(11); setColor(BLACK); pdf.text(t('hobbs_report_export_pdf_aircraft_section_title'), 14, y);
      y += 2; setFill(BLUE); pdf.rect(14, y, 30, 0.8, 'F'); y += 5;
      const colsAC = [
        { label: t('hobbs_report_export_pdf_aircraft_col_aircraft'),    x: 14  },
        { label: t('hobbs_report_export_pdf_aircraft_col_flights'),     x: 66  },
        { label: t('hobbs_report_export_pdf_aircraft_col_hours_flown'), x: 86  },
        { label: t('hobbs_meter'),                                       x: 112 },
        { label: t('hobbs_report_export_pdf_aircraft_col_remaining'),    x: 138 },
        { label: t('hobbs_report_export_pdf_aircraft_col_status'),       x: 166 },
      ];
      setFill(BLUE); pdf.rect(14, y, W - 28, 8, 'F');
      bold(7); setColor(WHITE); colsAC.forEach(c => pdf.text(c.label.toUpperCase(), c.x + 2, y + 5.2));
      y += 8;
      data.aircraft.forEach((ac, i) => {
        const rowH = 9, bg = i % 2 === 0 ? [248, 250, 252] : WHITE;
        setFill(bg); setDraw([226, 232, 240]); pdf.rect(14, y, W - 28, rowH, 'FD');
        const needsService = ac.hours_remaining <= 0;
        const alertSoon    = ac.hours_remaining > 0 && ac.hours_remaining <= 10;
        const statusColor  = needsService ? RED : alertSoon ? AMBER : GREEN;
        const statusText   = needsService ? t('overdue') : alertSoon ? t('upcoming') : 'OK';
        normal(8); setColor(BLACK); pdf.text(ac.name, colsAC[0].x + 2, y + 5.5);
        if (ac.registration) { small(6); setColor(GRAY); pdf.text(ac.registration, colsAC[0].x + 2, y + 8); }
        bold(8); setColor(ac.total_flights > 0 ? BLACK : GRAY);
        pdf.text(ac.total_flights > 0 ? String(ac.total_flights) : '—', colsAC[1].x + 2, y + 6);
        setColor(ac.hours_flown > 0 ? BLUE : GRAY);
        pdf.text(ac.hours_flown > 0 ? `${ac.hours_flown} hs` : '—', colsAC[2].x + 2, y + 6);
        normal(8); setColor(ac.hobbs_current > 0 ? BLACK : GRAY);
        pdf.text(ac.hobbs_current > 0 ? `${ac.hobbs_current} hs` : '—', colsAC[3].x + 2, y + 6);
        bold(8); setColor(statusColor);
        pdf.text(ac.hobbs_current > 0 ? `${ac.hours_remaining} hs` : '—', colsAC[4].x + 2, y + 6);
        if (ac.hobbs_current > 0) {
          setFill([...statusColor.map(v => Math.min(255, v + 180))]);
          pdf.roundedRect(colsAC[5].x + 1, y + 1.5, 24, 6, 2, 2, 'F');
          bold(7); setColor(statusColor); pdf.text(statusText, colsAC[5].x + 13, y + 5.8, { align: 'center' });
        } else { normal(7); setColor(GRAY); pdf.text(t('hobbs_report_export_pdf_no_data'), colsAC[5].x + 2, y + 6); }
        y += rowH;
      });
      y += 10;
      if (y > H - 70) { pdf.addPage(); y = 20; }

      // Tabla pilotos
      bold(11); setColor(BLACK); pdf.text(t('hobbs_report_export_pdf_pilots_section_title'), 14, y);
      y += 2; setFill(PURPLE); pdf.rect(14, y, 30, 0.8, 'F'); y += 5;
      const colsPilot = [
        { label: t('hobbs_report_export_pdf_pilot_col_pilot'),       x: 14 },
        { label: t('hobbs_report_export_pdf_pilot_col_flights'),     x: 86 },
        { label: t('hobbs_report_export_pdf_pilot_col_aircraft'),    x: 113 },
        { label: t('hobbs_report_export_pdf_pilot_col_hours_flown'), x: 145 },
      ];
      setFill(PURPLE); pdf.rect(14, y, W - 28, 8, 'F');
      bold(7); setColor(WHITE); colsPilot.forEach(c => pdf.text(c.label.toUpperCase(), c.x + 2, y + 5.2));
      y += 8;
      data.pilots.forEach((p, i) => {
        const rowH = 9, bg = i % 2 === 0 ? [248, 250, 252] : WHITE;
        setFill(bg); setDraw([226, 232, 240]); pdf.rect(14, y, W - 28, rowH, 'FD');
        normal(8); setColor(BLACK); pdf.text(p.name, colsPilot[0].x + 2, y + 5.5);
        small(6); setColor(GRAY); pdf.text(p.email || '', colsPilot[0].x + 2, y + 8);
        bold(9); setColor(BLACK);
        pdf.text(String(p.flight_count), colsPilot[1].x + 2, y + 6);
        pdf.text(String(p.aircraft_count), colsPilot[2].x + 2, y + 6);
        setColor(PURPLE); pdf.text(`${p.hours_flown} hs`, colsPilot[3].x + 2, y + 6);
        y += rowH;
      });
      if (data.pilots.length > 0) {
        setFill([237, 233, 254]); pdf.rect(14, y, W - 28, 9, 'F');
        bold(8); setColor([109, 40, 217]);
        pdf.text(t('hobbs_report_export_total_label'), colsPilot[0].x + 2, y + 6);
        pdf.text(String(data.totals.total_flights), colsPilot[1].x + 2, y + 6);
        pdf.text(`${Math.round(data.totals.total_hours * 10) / 10} hs`, colsPilot[3].x + 2, y + 6);
        y += 9;
      }

      // Footer (loop en todas las páginas)
      const disclaimerHobbs = 'Reporte gerencial generado por Condor360 — herramienta de asistencia administrativa. Los registros aquí listados no reemplazan los comprobantes oficiales de horómetros ni los procedimientos del fabricante. La responsabilidad operacional de cada vuelo es del Piloto al Mando.';
      const totalPagesHobbs = pdf.internal.getNumberOfPages();
      for (let p = 1; p <= totalPagesHobbs; p++) {
        pdf.setPage(p);
        // Disclaimer en gris justo arriba del rect NAVY
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text(disclaimerHobbs, W / 2, H - 16, { align: 'center', maxWidth: W - 28 });
        // Rect NAVY + brand
        setFill(NAVY); pdf.rect(0, H - 12, W, 12, 'F');
        small(7); setColor([147, 197, 253]); pdf.text('Condor360 · Aeroclubes y Escuelas de Aviación', W / 2, H - 5, { align: 'center' });
        setColor([71, 114, 182]);
        pdf.text(clubName ? `${clubName} · ${monthLabel}` : monthLabel, 14, H - 5);
        pdf.text(`${t('hobbs_report_export_pdf_generated_by_label')} ${user?.name || t('hobbs_report_export_pdf_default_admin_short')} · ${t('hobbs_report_export_pdf_page_label')} ${p}/${totalPagesHobbs}`, W - 14, H - 5, { align: 'right' });
      }
      pdf.setTextColor(0, 0, 0);

      pdf.save(`Condor360_Reporte_${MONTHS[month - 1]}_${year}.pdf`);
    } catch (err) {
      console.error('Error exportando PDF:', err);
    } finally { setExporting(false); }
  };

  // ── Exportar Excel ──────────────────────────────────────────────
  const handleExportExcel = () => {
    if (!data) return;
    setExportingXls(true);
    try {
      const monthLabel = `${MONTHS[month - 1]} ${year}`;
      const generated  = now.toLocaleString('es-AR', { dateStyle: 'long', timeStyle: 'short' });
      const wb         = XLSX.utils.book_new();

      // ── Hoja 1: Resumen ────────────────────────────────────────
      const resumenData = [
        [`CONDOR360 — ${t('hobbs_report_export_xls_brand_subtitle')}`],
        [],
        [t('hobbs_report_export_xls_meta_club'),         clubName || t('hobbs_report_export_pdf_no_club')],
        [t('hobbs_report_export_xls_meta_period'),       monthLabel],
        [t('hobbs_report_export_xls_meta_generated_by'), user?.name || t('hobbs_report_export_pdf_default_admin')],
        [t('hobbs_report_export_xls_meta_generated_at'), generated],
        [],
        [t('hobbs_report_export_xls_summary_section_title')],
        [t('hobbs_report_export_xls_stat_hours_flown'),     `${Math.round(data.totals.total_hours * 10) / 10} hs`],
        [t('hobbs_report_export_xls_stat_flights'),         data.totals.total_flights],
        [t('hobbs_report_export_xls_stat_active_aircraft'), data.totals.active_aircraft],
        [t('hobbs_report_export_xls_stat_fleet'),           data.aircraft.length],
        [t('hobbs_report_export_xls_stat_pilots'),          data.pilots.length],
        [t('hobbs_report_export_xls_stat_need_service'),    data.totals.aircraft_needing_service],
      ];
      const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
      // Estilos básicos — ancho de columnas
      wsResumen['!cols'] = [{ wch: 28 }, { wch: 35 }];
      XLSX.utils.book_append_sheet(wb, wsResumen, t('hobbs_report_export_xls_sheet_summary'));

      // ── Hoja 2: Aeronaves ──────────────────────────────────────
      const acHeaders = [
        t('hobbs_report_export_xls_aircraft_col_aircraft'),
        t('hobbs_report_export_xls_aircraft_col_registration'),
        t('hobbs_report_export_xls_aircraft_col_flights'),
        t('hobbs_report_export_xls_aircraft_col_hours_flown'),
        t('hobbs_report_export_xls_aircraft_col_total_hobbs'),
        t('hobbs_report_export_xls_aircraft_col_last_service'),
        t('hobbs_report_export_xls_aircraft_col_service_interval'),
        t('hobbs_report_export_xls_aircraft_col_remaining'),
        t('hobbs_report_export_xls_aircraft_col_status'),
      ];
      const acRows = data.aircraft.map(ac => {
        const needsService = ac.hours_remaining <= 0;
        const alertSoon    = ac.hours_remaining > 0 && ac.hours_remaining <= 10;
        const estado       = ac.hobbs_current > 0
          ? (needsService ? t('overdue') : alertSoon ? t('upcoming') : 'OK')
          : t('hobbs_report_export_pdf_no_data');
        return [
          ac.name,
          ac.registration || '—',
          ac.total_flights  > 0 ? ac.total_flights  : 0,
          ac.hours_flown    > 0 ? ac.hours_flown    : 0,
          ac.hobbs_current  > 0 ? ac.hobbs_current  : '—',
          ac.last_service_date
            ? new Date(ac.last_service_date).toLocaleDateString('es-AR')
            : '—',
          ac.service_interval_hours || 100,
          ac.hobbs_current > 0 ? ac.hours_remaining : '—',
          estado,
        ];
      });
      const wsAircraft = XLSX.utils.aoa_to_sheet([acHeaders, ...acRows]);
      wsAircraft['!cols'] = [
        { wch: 22 }, { wch: 12 }, { wch: 10 }, { wch: 14 },
        { wch: 16 }, { wch: 16 }, { wch: 22 }, { wch: 18 }, { wch: 12 },
      ];
      XLSX.utils.book_append_sheet(wb, wsAircraft, t('hobbs_report_export_xls_sheet_aircraft'));

      // ── Hoja 3: Pilotos ────────────────────────────────────────
      const pilotHeaders = [
        t('hobbs_report_export_xls_pilot_col_pilot'),
        t('hobbs_report_export_xls_pilot_col_email'),
        t('hobbs_report_export_xls_pilot_col_flights'),
        t('hobbs_report_export_xls_pilot_col_aircraft_distinct'),
        t('hobbs_report_export_xls_pilot_col_hours_flown'),
      ];
      const pilotRows    = data.pilots.map(p => [
        p.name,
        p.email || '—',
        p.flight_count,
        p.aircraft_count,
        p.hours_flown,
      ]);
      // Fila de totales
      const pilotTotals = [
        t('hobbs_report_export_total_label'), '',
        data.totals.total_flights,
        '',
        Math.round(data.totals.total_hours * 10) / 10,
      ];
      const wsPilots = XLSX.utils.aoa_to_sheet([pilotHeaders, ...pilotRows, [], pilotTotals]);
      wsPilots['!cols'] = [{ wch: 28 }, { wch: 30 }, { wch: 10 }, { wch: 20 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsPilots, t('hobbs_report_export_xls_sheet_pilots'));

      // Guardar
      XLSX.writeFile(wb, `Condor360_Reporte_${MONTHS[month - 1]}_${year}.xlsx`);
    } catch (err) {
      console.error('Error exportando Excel:', err);
    } finally { setExportingXls(false); }
  };

  const handleExportFlightLogExcel = () => {
    if (!flightLog?.entries?.length) return;
    const monthLabel = `${MONTHS[month - 1]} ${year}`;
    const wb = XLSX.utils.book_new();
    const headers = [
      t('hobbs_report_export_log_col_n'),
      t('hobbs_report_export_log_col_date'),
      t('hobbs_report_export_log_col_time'),
      t('hobbs_report_export_log_col_pilot'),
      t('hobbs_report_export_log_col_email'),
      t('hobbs_report_export_log_col_aircraft'),
      t('hobbs_report_export_log_col_registration'),
      t('hobbs_report_export_log_col_club'),
      t('hobbs_report_export_log_col_hobbs_start'),
      t('hobbs_report_export_log_col_hobbs_end'),
      t('hobbs_report_export_log_col_hours_flown'),
      'Finalidad',
      'Instructor',
      'Aterrizajes',
      'Forma de pago',
      t('hobbs_report_export_log_col_weather'),
      t('hobbs_report_export_log_col_notes'),
    ];
    const rows = flightLog.entries.map((e, i) => [
      i + 1,
      new Date(e.flight_date).toLocaleDateString('es-AR'),
      e.flight_time || '—',
      e.pilot_name,
      e.pilot_email || '—',
      e.aircraft,
      e.registration || '—',
      e.organization_name || e.club_name || '—',
      e.hobbs_start,
      e.hobbs_end,
      e.hours_flown,
      purposeLabel(e),
      e.instructor_name || '—',
      e.landings != null ? e.landings : '—',
      e.payment_method || '—',
      e.weather || '—',
      e.notes || '—',
    ]);
    const totalRow = ['', '', '', '', '', '', '', t('hobbs_report_export_total_label'), '', '', flightLog.totals.total_hours.toFixed(1) + ' hs', '', '', '', '', '', ''];
    const ws = XLSX.utils.aoa_to_sheet([
      [`CONDOR360 — ${t('hobbs_report_export_log_brand_subtitle')} — ${monthLabel}`],
      [],
      headers,
      ...rows,
      [],
      totalRow,
    ]);
    ws['!cols'] = [
      {wch:4},{wch:12},{wch:8},{wch:24},{wch:28},{wch:22},{wch:12},
      {wch:22},{wch:12},{wch:10},{wch:14},
      {wch:18},{wch:22},{wch:11},{wch:16},
      {wch:16},{wch:28}
    ];
    XLSX.utils.book_append_sheet(wb, ws, t('hobbs_report_export_log_sheet'));
    XLSX.writeFile(wb, `Condor360_LibroVuelos_${MONTHS[month-1]}_${year}.xlsx`);
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
    if (isCurrentMonth) return;
    if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1);
  };
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const card = {
    background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
    borderRadius: '20px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: 900, color: darkMode ? 'white' : '#0f172a', letterSpacing: '-0.5px' }}>Reporte Mensual</h1>
          <p style={{ margin: 0, fontSize: '14px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>Horas de vuelo y estado de horómetros</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
            <MapPin style={{ width: '12px', height: '12px', color: loadingGeo ? '#f59e0b' : clubName ? '#16a34a' : '#94a3b8' }} />
            <span style={{ fontSize: '11px', color: loadingGeo ? '#f59e0b' : clubName ? '#16a34a' : '#94a3b8', fontWeight: 600 }}>
              {loadingGeo ? t('hobbs_report_preview_loading_detecting_org') : clubName || 'Aeroclub no detectado'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Botones de exportación */}
          {data && (
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* PDF */}
              <motion.button onClick={handleExportPDF} disabled={exporting}
                whileHover={!exporting ? { scale: 1.02 } : {}} whileTap={!exporting ? { scale: 0.97 } : {}}
                style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 16px', borderRadius: '12px', border: 'none', cursor: exporting ? 'not-allowed' : 'pointer', background: exporting ? 'rgba(37,99,235,0.3)' : 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white', fontWeight: 700, fontSize: '13px', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                {exporting
                  ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid transparent', borderTopColor: 'white' }} />Exportando...</>
                  : <><Download style={{ width: '14px', height: '14px' }} />PDF</>
                }
              </motion.button>

              {/* Excel */}
              <motion.button onClick={handleExportExcel} disabled={exportingXls}
                whileHover={!exportingXls ? { scale: 1.02 } : {}} whileTap={!exportingXls ? { scale: 0.97 } : {}}
                style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 16px', borderRadius: '12px', border: 'none', cursor: exportingXls ? 'not-allowed' : 'pointer', background: exportingXls ? 'rgba(22,163,74,0.3)' : 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', fontWeight: 700, fontSize: '13px', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                {exportingXls
                  ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid transparent', borderTopColor: 'white' }} />Exportando...</>
                  : <><FileSpreadsheet style={{ width: '14px', height: '14px' }} />Excel</>
                }
              </motion.button>
            </div>
          )}

          {/* Navegación de mes/año — dropdowns + flechas opcionales */}
          <PeriodPicker
            month={month} year={year}
            onMonthChange={setMonth} onYearChange={setYear}
            onPrev={prevMonth} onNext={nextMonth}
            isCurrentMonth={isCurrentMonth}
            darkMode={darkMode}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Loader style={{ width: '32px', height: '32px', color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : error ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', borderRadius: '16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <AlertCircle style={{ width: '18px', height: '18px', color: '#ef4444' }} />
          <p style={{ margin: 0, fontSize: '14px', color: '#ef4444' }}>{error}</p>
        </div>
      ) : data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <StatCard icon={<Clock />} label="Horas voladas" value={`${Math.round(data.totals.total_hours * 10) / 10} hs`} sub={`${data.totals.total_flights} vuelos registrados`} color="#2563eb" darkMode={darkMode} />
            <StatCard icon={<Plane />} label="Aeronaves activas" value={data.totals.active_aircraft} sub={`de ${data.aircraft.length} en flota`} color="#7c3aed" darkMode={darkMode} />
            <StatCard icon={<Users />} label="Pilotos volaron" value={data.pilots.length} sub="en este periodo" color="#0891b2" darkMode={darkMode} />
            <StatCard icon={<Wrench />} label="Necesitan service" value={data.totals.aircraft_needing_service} sub="service vencido o proximo" color={data.totals.aircraft_needing_service > 0 ? '#ef4444' : '#16a34a'} darkMode={darkMode} />
          </div>

          {/* Tabla */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: isMobile ? 'nowrap' : 'wrap', overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch', maxWidth: isMobile ? '100%' : undefined }}>
                {[
                { id: 'aircraft',  label: '✈️ Por aeronave', color: '#2563eb' },
                { id: 'pilots',    label: '👤 Por piloto',   color: '#7c3aed' },
                { id: 'flightlog', label: '📒 Libro de vuelos', color: '#16a34a' },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  style={{ padding: '8px 16px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '13px', fontFamily: 'inherit', background: tab === t.id ? t.color : (darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9'), color: tab === t.id ? 'white' : (darkMode ? 'rgba(255,255,255,0.5)' : '#64748b'), transition: 'all 0.2s', flexShrink: isMobile ? 0 : undefined, whiteSpace: 'nowrap' }}>
                  {t.label}
                </button>
              ))}
              </div>
              {tab === 'flightlog' && (() => {
                const allEntries  = flightLog?.entries || [];
                const hasImported = allEntries.some(e => e.import_id);
                const visibleEntries = showImported ? allEntries : allEntries.filter(e => !e.import_id);
                return (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
                      title={sortOrder === 'desc' ? 'Más reciente primero (click para invertir)' : 'Más antigua primero (click para invertir)'}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 10, border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.04)' : 'white', color: darkMode ? 'rgba(255,255,255,0.7)' : '#475569', cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit' }}>
                      <ArrowDownUp style={{ width: 12, height: 12 }} />
                      {sortOrder === 'desc' ? 'Reciente primero' : 'Antigua primero'}
                    </button>
                    {hasImported && (
                      <button onClick={() => setShowImported(v => !v)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 10, border: '1px solid rgba(124,58,237,0.3)', background: showImported ? 'rgba(124,58,237,0.1)' : 'transparent', color: '#7c3aed', cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit' }}>
                        <UploadIcon style={{ width: 11, height: 11 }} />
                        {showImported ? 'Ocultar importados' : 'Mostrar importados'}
                      </button>
                    )}
                    {visibleEntries.length > 0 && (
                      <motion.button onClick={handleExportFlightLogExcel}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', fontWeight: 700, fontSize: '12px', fontFamily: 'inherit' }}>
                        <FileSpreadsheet style={{ width: '13px', height: '13px' }} />
                        Exportar Excel
                      </motion.button>
                    )}
                  </div>
                );
              })()}
            </div>

            {tab === 'aircraft' && (
              <div style={{ overflowX: 'auto' }}>
                {data.aircraft.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
                    <Plane style={{ width: '36px', height: '36px', margin: '0 auto 10px', display: 'block', opacity: 0.4 }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>Sin vuelos registrados en {MONTHS[month-1]} {year}</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {[
                          t('hobbs_report_preview_aircraft_table_header_aircraft'),
                          t('hobbs_report_preview_aircraft_table_header_flights'),
                          t('hobbs_report_preview_aircraft_table_header_hours'),
                          t('hobbs_report_preview_aircraft_table_header_horometer_total'),
                          t('hobbs_report_preview_aircraft_table_header_remaining_service'),
                          t('hobbs_report_preview_aircraft_table_header_status'),
                        ].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.aircraft.map((ac, i) => {
                        const needsService = ac.hours_remaining <= 0;
                        const alertSoon    = ac.hours_remaining > 0 && ac.hours_remaining <= 10;
                        return (
                          <motion.tr key={ac.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                            style={{ background: i % 2 === 0 ? (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') : 'transparent' }}>
                            <td style={{ padding: '12px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>
                              <p style={{ margin: 0, fontWeight: 700, fontSize: '13px', color: darkMode ? 'white' : '#0f172a' }}>{ac.name}</p>
                              {ac.registration && <p style={{ margin: '2px 0 0', fontSize: '11px', fontFamily: 'monospace', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>{ac.registration}</p>}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc' }}>
                              <span style={{ fontSize: '14px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151' }}>
                                {ac.total_flights > 0 ? ac.total_flights : <span style={{ opacity: 0.3 }}>—</span>}
                              </span>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc' }}>
                              <span style={{ fontSize: '14px', fontWeight: 700, color: ac.hours_flown > 0 ? '#2563eb' : (darkMode ? 'rgba(255,255,255,0.25)' : '#cbd5e1') }}>
                                {ac.hours_flown > 0 ? `${ac.hours_flown} hs` : '—'}
                              </span>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc' }}>
                              <span style={{ fontSize: '13px', fontWeight: 600, color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151' }}>
                                {ac.hobbs_current > 0 ? `${ac.hobbs_current} hs` : '—'}
                              </span>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc' }}>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: needsService ? '#ef4444' : alertSoon ? '#d97706' : '#16a34a' }}>
                                {ac.hobbs_current > 0 ? `${ac.hours_remaining} hs` : '—'}
                              </span>
                            </td>
                            <td style={{ padding: '12px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc' }}>
                              {ac.hobbs_current > 0 ? (
                                <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '20px', background: needsService ? 'rgba(239,68,68,0.1)' : alertSoon ? 'rgba(217,119,6,0.1)' : 'rgba(22,163,74,0.1)', color: needsService ? '#ef4444' : alertSoon ? '#d97706' : '#16a34a', border: `1px solid ${needsService ? 'rgba(239,68,68,0.2)' : alertSoon ? 'rgba(217,119,6,0.2)' : 'rgba(22,163,74,0.2)'}`, whiteSpace: 'nowrap' }}>
                                  {needsService ? '🚨 Vencido' : alertSoon ? '⚠️ Proximo' : '✅ OK'}
                                </span>
                              ) : <span style={{ opacity: 0.3, fontSize: '12px' }}>Sin datos</span>}
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {tab === 'pilots' && (
              <div style={{ overflowX: 'auto' }}>
                {data.pilots.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
                    <Users style={{ width: '36px', height: '36px', margin: '0 auto 10px', display: 'block', opacity: 0.4 }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>Sin pilotos registrados en {MONTHS[month-1]} {year}</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {[
                          t('hobbs_report_preview_pilot_table_header_pilot'),
                          t('hobbs_report_preview_pilot_table_header_flights'),
                          t('hobbs_report_preview_pilot_table_header_aircraft'),
                          t('hobbs_report_preview_pilot_table_header_hours'),
                        ].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.pilots.map((p, i) => (
                        <motion.tr key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                          style={{ background: i % 2 === 0 ? (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') : 'transparent' }}>
                          <td style={{ padding: '12px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: '13px', color: darkMode ? 'white' : '#0f172a' }}>{p.name}</p>
                            <p style={{ margin: '2px 0 0', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>{p.email}</p>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc' }}>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151' }}>{p.flight_count}</span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc' }}>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151' }}>{p.aircraft_count}</span>
                          </td>
                          <td style={{ padding: '12px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed' }}>{p.hours_flown} hs</span>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: darkMode ? 'rgba(124,58,237,0.08)' : 'rgba(124,58,237,0.04)' }}>
                        <td style={{ padding: '12px', fontSize: '12px', fontWeight: 800, color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>TOTAL</td>
                        <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: 800, color: darkMode ? 'white' : '#0f172a' }}>{data.totals.total_flights}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }} />
                        <td style={{ padding: '12px', fontSize: '14px', fontWeight: 800, color: '#7c3aed' }}>{Math.round(data.totals.total_hours * 10) / 10} hs</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}
            {tab === 'flightlog' && (() => {
              // PR #6 (Vision) — filtro de vuelos importados
              const allEntries        = flightLog?.entries || [];
              const visibleEntries    = (showImported ? allEntries : allEntries.filter(e => !e.import_id))
                .slice()
                .sort((a, b) => {
                  const ta = new Date(a.flight_date).getTime();
                  const tb = new Date(b.flight_date).getTime();
                  return sortOrder === 'desc' ? tb - ta : ta - tb;
                });
              const visibleTotalHours = visibleEntries.reduce((a, e) => a + parseFloat(e.hours_flown || 0), 0);
              return (
              <div>
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>
                    {flightLog ? `${visibleEntries.length} vuelos · ${visibleTotalHours.toFixed(1)} hs totales` : 'Cargando...'}
                  </p>
                </div>
                {loadingLog ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <Loader style={{ width: '28px', height: '28px', color: '#16a34a', animation: 'spin 1s linear infinite' }} />
                  </div>
                ) : !visibleEntries.length ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
                    <BookOpen style={{ width: '36px', height: '36px', margin: '0 auto 10px', display: 'block', opacity: 0.4 }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>Sin vuelos registrados en {MONTHS[month-1]} {year}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.6 }}>Los vuelos aparecen cuando se registra el horómetro al completar un chequeo</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr>
                          {[
                            t('hobbs_report_preview_log_table_header_n'),
                            t('hobbs_report_preview_log_table_header_date'),
                            t('hobbs_report_preview_log_table_header_pilot'),
                            t('hobbs_report_preview_log_table_header_aircraft'),
                            t('hobbs_report_preview_log_table_header_horometer_initial'),
                            t('hobbs_report_preview_log_table_header_horometer_final'),
                            t('hobbs_report_preview_log_table_header_hours'),
                            'Finalidad',
                            'Instructor',
                            'Atrz.',
                            'Pago',
                            t('hobbs_report_preview_log_table_header_aeroclub'),
                          ].map(h => (
                            <th key={h} style={{ padding: '10px 10px', textAlign: 'left', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleEntries.map((e, i) => (
                          <tr key={e.id} style={{ background: i % 2 === 0 ? (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') : 'transparent' }}>
                            <td style={{ padding: '10px', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc' }}>{i+1}</td>
                            <td style={{ padding: '10px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>
                              <p style={{ margin: 0, fontWeight: 600, color: darkMode ? 'rgba(255,255,255,0.8)' : '#374151' }}>{new Date(e.flight_date).toLocaleDateString('es-AR')}</p>
                              <p style={{ margin: 0, fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>{e.flight_time}</p>
                            </td>
                            <td style={{ padding: '10px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <p style={{ margin: 0, fontWeight: 600, color: darkMode ? 'white' : '#0f172a' }}>{e.pilot_name || '—'}</p>
                                {e.import_id && (
                                  <span title="Vuelo digitalizado de una planilla histórica"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 99, background: 'rgba(124,58,237,0.12)', color: '#7c3aed', border: '1px solid rgba(124,58,237,0.35)' }}>
                                    <UploadIcon style={{ width: 8, height: 8 }} /> IMPORTADO
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '10px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>
                              <p style={{ margin: 0, color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151' }}>{e.aircraft}</p>
                              {e.registration && <p style={{ margin: 0, fontSize: '10px', fontFamily: 'monospace', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>{e.registration}</p>}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc' }}>{e.hobbs_start}</td>
                            <td style={{ padding: '10px', textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc' }}>{e.hobbs_end}</td>
                            <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800, color: '#16a34a', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc' }}>{e.hours_flown} hs</td>
                            <td style={{ padding: '10px', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>{purposeLabel(e)}</td>
                            <td style={{ padding: '10px', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>{e.instructor_name || '—'}</td>
                            <td style={{ padding: '10px', textAlign: 'center', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc' }}>{e.landings != null ? e.landings : '—'}</td>
                            <td style={{ padding: '10px', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>{e.payment_method || '—'}</td>
                            <td style={{ padding: '10px', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc', whiteSpace: 'nowrap' }}>{e.organization_name || e.club_name || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: darkMode ? 'rgba(22,163,74,0.08)' : 'rgba(22,163,74,0.04)' }}>
                          <td colSpan={6} style={{ padding: '10px', fontSize: '12px', fontWeight: 800, color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>TOTAL — {visibleEntries.length} vuelos</td>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800, fontSize: '14px', color: '#16a34a' }}>{visibleTotalHours.toFixed(1)} hs</td>
                          <td colSpan={5} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
              );
            })()}
          </div>
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminHobbsReport;
